import type { ICEServer } from './protocol'

export class SignalAPI {
  private baseUrl: string
  private timeoutMs = 15000

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), this.timeoutMs)
    let res: Response
    try {
      res = await fetch(`${this.baseUrl}${path}`, { ...options, signal: controller.signal })
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        throw new Error(`API request timeout after ${this.timeoutMs}ms: ${path}`)
      }
      throw e
    } finally {
      clearTimeout(timer)
    }
    if (!res.ok) {
      let message = `${res.status} ${res.statusText}`
      try {
        const body = await res.json()
        if (body && typeof body.error === 'string') {
          message += `: ${body.error}`
        }
      } catch {
        // 响应体非 JSON，仅保留状态码信息
      }
      throw new Error(`API request failed: ${message}`)
    }
    return res.json()
  }

  async getICEServers(): Promise<ICEServer[]> {
    const data = await this.request<{ iceServers?: ICEServer[]; ice_servers?: ICEServer[] }>('/api/ice-servers')
    return data.iceServers || data.ice_servers || []
  }

  async getDevices(): Promise<any[]> {
    const data = await this.request<{ devices?: any[] }>('/api/devices')
    return data.devices || []
  }

  async getOperators(): Promise<any[]> {
    const data = await this.request<{ operators?: any[] }>('/api/operators')
    return data.operators || []
  }

  async getCalls(params?: {
    deviceId?: string
    operatorId?: string
    state?: string
    limit?: number
    offset?: number
  }): Promise<{ count: number; calls: any[]; limit: number; offset: number }> {
    const query = new URLSearchParams()
    if (params?.deviceId) query.set('device_id', params.deviceId)
    if (params?.operatorId) query.set('operator_id', params.operatorId)
    if (params?.state) query.set('state', params.state)
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const qs = query.toString()
    return this.request(`/api/calls${qs ? '?' + qs : ''}`)
  }

  /** 按设备获取告警/通话历史 */
  async getCallsByDevice(deviceId: string, params?: { limit?: number; offset?: number; state?: string }): Promise<{ count: number; calls: any[]; limit: number; offset: number }> {
    return this.getCalls({ ...params, deviceId })
  }

  /** 按接警员获取通话历史 */
  async getCallsByOperator(operatorId: string, params?: { limit?: number; offset?: number; state?: string }): Promise<{ count: number; calls: any[]; limit: number; offset: number }> {
    return this.getCalls({ ...params, operatorId })
  }

  async getActiveCalls(): Promise<any[]> {
    const data = await this.request<{ calls?: any[] }>('/api/calls/active')
    return data.calls || []
  }

  async getCallStats(): Promise<any> {
    return this.request('/api/calls/stats')
  }

  async getRecordings(callId: string): Promise<any[]> {
    const data = await this.request<{ recordings?: any[] }>(`/api/calls/${callId}/recordings`)
    return data.recordings || []
  }

  async forceHangup(callId: string, token: string): Promise<void> {
    await this.request(`/api/calls/${callId}/hangup`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  }

  async login(id: string, password: string): Promise<{ token: string; operator: any }> {
    return this.request('/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, password }),
    })
  }

  async resetOperatorPassword(operatorId: string, newPassword: string, token: string): Promise<{ ok: boolean }> {
    return this.request(`/api/admin/operators/${operatorId}/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ new_password: newPassword }),
    })
  }

  async createOperator(operator: { id: string; name: string; password: string; role?: string }, token: string): Promise<{ ok: boolean }> {
    return this.request('/api/admin/operators', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(operator),
    })
  }

  async updateOperator(operatorId: string, data: { name?: string; role?: string; disabled?: boolean }, token: string): Promise<{ ok: boolean }> {
    return this.request(`/api/admin/operators/${operatorId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    })
  }

  async deleteOperator(operatorId: string, token: string): Promise<{ ok: boolean }> {
    return this.request(`/api/admin/operators/${operatorId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  }

  async listAllOperators(token: string): Promise<{ operators: any[] }> {
    return this.request('/api/admin/operators/all', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  }
}
